import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Eye, EyeOff, Terminal, User, Mail, Lock, ArrowRight, Shield, ArrowLeft, Loader2, Home } from 'lucide-react'
import { toast } from 'sonner'

// Type declaration for Google reCAPTCHA
declare global {
  interface Window {
    grecaptcha: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

type AuthMode = 'login' | 'register'

function PasswordStrength({ password }: { password: string }) {
  const getStrength = (pw: string): { score: number; label: string; color: string } => {
    let score = 0
    if (pw.length >= 6) score++
    if (pw.length >= 10) score++
    if (/[A-Z]/.test(pw)) score++
    if (/[0-9]/.test(pw)) score++
    if (/[^A-Za-z0-9]/.test(pw)) score++

    if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500' }
    if (score <= 2) return { score: 2, label: 'Fair', color: 'bg-orange-500' }
    if (score <= 3) return { score: 3, label: 'Good', color: 'bg-yellow-500' }
    if (score <= 4) return { score: 4, label: 'Strong', color: 'bg-green-500' }
    return { score: 5, label: 'Very Strong', color: 'bg-emerald-500' }
  }

  if (!password) return null

  const { score, label, color } = getStrength(password)

  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= score ? color : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

function VerificationPrompt({ email, onBack }: { email: string; onBack: () => void }) {
  const { resendVerification } = useAuth()
  const [cooldown, setCooldown] = useState(0)
  const [resending, setResending] = useState(false)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const handleResend = async () => {
    if (cooldown > 0 || resending) return
    setResending(true)
    const result = await resendVerification(email)
    setResending(false)
    if (result.success) {
      toast.success('Verification email sent!')
      setCooldown(60)
    } else {
      toast.error(result.error || 'Failed to resend')
      if (result.retryAfter) setCooldown(result.retryAfter)
    }
  }

  return (
    <Card className="glass">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <Mail className="h-12 w-12 text-primary" />
        </div>
        <CardTitle>Check Your Email</CardTitle>
        <CardDescription>
          We sent a verification link to <strong className="text-foreground">{email}</strong>. Click the link in the email to verify your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          className="w-full"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
        >
          {resending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending...
            </span>
          ) : cooldown > 0 ? (
            `Resend in ${cooldown}s`
          ) : (
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Resend Verification Email
            </span>
          )}
        </Button>
        <Button variant="outline" className="w-full" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          The verification link expires in 24 hours
        </p>
      </CardContent>
    </Card>
  )
}

export function Auth() {
  const navigate = useNavigate()
  const { login, register, needsVerification, verificationEmail, clearVerification } = useAuth()
  const [mode, setMode] = useState<AuthMode>('login')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Get reCAPTCHA v3 token
    let recaptchaToken = ''
    try {
      if (window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute('6LdQdHQsAAAAANO1pqBWUXSmbOq12jJu6t1-iGNM', { action: mode === 'register' ? 'register' : 'login' })
      }
    } catch (err) {
      console.error('reCAPTCHA error:', err)
    }

    if (mode === 'register') {
      if (form.password !== form.confirmPassword) {
        toast.error('Passwords do not match')
        setLoading(false)
        return
      }
      if (form.password.length < 6) {
        toast.error('Password must be at least 6 characters')
        setLoading(false)
        return
      }
      if (form.username.length < 3) {
        toast.error('Username must be at least 3 characters')
        setLoading(false)
        return
      }

      const result = await register(form.username, form.email, form.password, recaptchaToken)
      setLoading(false)
      if (result.success) {
        if (!result.needsVerification) {
          toast.success('Account created successfully!')
          navigate('/')
        }
      } else {
        toast.error(result.error || 'Registration failed')
      }
    } else {
      const result = await login(form.email, form.password, recaptchaToken)
      setLoading(false)
      if (result.success) {
        toast.success('Welcome back!')
        navigate('/')
      } else if (!result.needsVerification) {
        toast.error(result.error || 'Login failed')
      }
    }
  }

  const updateForm = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode)
    setForm({ email: '', password: '', confirmPassword: '', username: '' })
    setShowPassword(false)
    clearVerification()
  }

  const handleBackFromVerification = () => {
    clearVerification()
  }

  // Show verification prompt if needed
  if (needsVerification && verificationEmail) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 border border-primary/30 shadow-glow">
              <Terminal className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-primary glow-text">SCB|PH Terminal</h1>
            <p className="text-sm text-muted-foreground font-mono">
              <span className="text-primary">{'>'}</span> Email verification required
            </p>
          </div>
          <VerificationPrompt email={verificationEmail} onBack={handleBackFromVerification} />
          <div className="text-center space-y-1">
            <div className="text-xs text-muted-foreground font-mono">
              <span className="text-primary">{'>'}</span> Secure connection established
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center space-y-3">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 border border-primary/30 shadow-glow">
            <Terminal className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-primary glow-text">SCB|PH Terminal</h1>
          <p className="text-sm text-muted-foreground font-mono">
            <span className="text-primary">{'>'}</span>{' '}
            {mode === 'login' ? 'Authenticate to access terminal' : 'Register new terminal access'}
          </p>
        </div>

        {/* Mode Switcher */}
        <div className="flex bg-card border border-border rounded-lg p-1">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
              mode === 'login'
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
              mode === 'register'
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Register
          </button>
        </div>

        {/* Auth Card */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {mode === 'login' ? (
                <>
                  <Lock className="h-5 w-5 text-primary" />
                  Sign In
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5 text-primary" />
                  Create Account
                </>
              )}
            </CardTitle>
            <CardDescription>
              {mode === 'login'
                ? 'Enter your credentials to continue'
                : 'Fill in your details to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      placeholder="Choose a username"
                      value={form.username}
                      onChange={(e) => updateForm('username', e.target.value)}
                      className="pl-10"
                      required
                      minLength={3}
                      maxLength={20}
                      pattern="[a-zA-Z0-9_]+"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Letters, numbers, underscores only</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{mode === 'login' ? 'Email or Username' : 'Email'}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type={mode === 'login' ? 'text' : 'email'}
                    placeholder={mode === 'login' ? 'Enter email or username' : 'Enter email'}
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={mode === 'login' ? 'Enter password' : 'Min 6 characters'}
                    value={form.password}
                    onChange={(e) => updateForm('password', e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-10 w-10"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {mode === 'register' && <PasswordStrength password={form.password} />}
              </div>

              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={form.confirmPassword}
                      onChange={(e) => updateForm('confirmPassword', e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <p className="text-xs text-destructive">Passwords do not match</p>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {mode === 'login' ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>

            {mode === 'register' && (
              <p className="mt-4 text-xs text-muted-foreground text-center">
                First account registered automatically becomes admin
              </p>
            )}

            {/* reCAPTCHA notice */}
            <p className="mt-4 text-xs text-muted-foreground flex items-center justify-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield w-3 h-3"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></svg>
              Protected by reCAPTCHA
            </p>
          </CardContent>
        </Card>

        {/* Go to Dashboard */}
        <Link to="/" className="block">
          <Button variant="outline" className="w-full">
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
        </Link>

        {/* Terminal decoration */}
        <div className="text-center space-y-1">
          <div className="text-xs text-muted-foreground font-mono">
            <span className="text-primary">{'>'}</span> Secure connection established
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            <span className="text-green-500">$</span> Access levels: user / member
          </div>
        </div>
      </div>
    </div>
  )
}
